import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";

interface ObserverEmailModalProps {
  open: boolean;
  onSubmit: (email: string) => void;
  loading: boolean;
  errorMessage?: string;
}

export function ObserverEmailModal({
  open,
  onSubmit,
  loading,
  errorMessage,
}: ObserverEmailModalProps): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.trim()) {
      setLocalError(t("observer.emailModal.emailRequired"));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setLocalError(t("observer.emailModal.invalidEmail"));
      return;
    }
    setLocalError("");
    onSubmit(email.trim());
  };

  return (
    <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={open}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VisibilityIcon color="primary" />
          <Typography component="span" fontWeight="500" variant="h6">
            {t("observer.emailModal.title")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 3 }} variant="body2">
            {t("observer.emailModal.description")}
          </Typography>
          {(errorMessage || localError) ? <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || localError}
            </Alert> : null}
          <Box sx={{ mt: 1 }}>
            <TextField
              disabled={loading}
              fullWidth
              label={t("observer.emailModal.emailLabel")}
              onChange={(e) => {
                setEmail(e.target.value);
                if (localError) setLocalError("");
              }}
              placeholder="exemplo@empresa.com"
              required
              type="email"
              value={email}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            color="primary"
            disabled={loading}
            fullWidth
            sx={{ borderRadius: "12px", py: 1.2 }}
            type="submit"
            variant="contained"
          >
            {loading ? "..." : t("observer.emailModal.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ObserverEmailModal;
